import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, BadgeCheck, FlaskConical, Loader2, ShieldAlert } from "lucide-react";
import { api } from "../api";
import { Button } from "../components/ui/button";

const CONTROL_PARCEL = "146505_8.0103.33/3"; // ULDK-verified control (Warszawa)

const STATUS_STYLE = {
  DETECTED: "bg-amber-100 text-amber-900 border-amber-300",
  NOT_DETECTED: "bg-emerald-50 text-emerald-800 border-emerald-300",
  SOURCE_UNAVAILABLE: "bg-rose-50 text-rose-800 border-rose-300",
  INSUFFICIENT_DATA: "bg-slate-100 text-slate-600 border-slate-300",
  REQUIRES_PROFESSIONAL_VERIFICATION: "bg-blue-50 text-blue-800 border-blue-300",
};
const STATUS_LABEL = {
  DETECTED: "WYKRYTO",
  NOT_DETECTED: "NIE WYKRYTO",
  SOURCE_UNAVAILABLE: "ŹRÓDŁO NIEDOSTĘPNE",
  INSUFFICIENT_DATA: "NIEWYSTARCZAJĄCE DANE",
  REQUIRES_PROFESSIONAL_VERIFICATION: "WYMAGA WERYFIKACJI SPECJALISTY",
};

const StatusBadge = ({ status, testId }) => (
  <span data-testid={testId}
        className={`inline-block rounded-full border px-2.5 py-0.5 text-[11px] font-bold tracking-wide ${STATUS_STYLE[status]}`}>
    {STATUS_LABEL[status]}
  </span>
);

const Provenance = ({ children }) => (
  <p className="mt-2 border-l-2 border-slate-200 pl-2 text-[11px] leading-relaxed text-slate-400">{children}</p>
);

const Section = ({ title, status, statusTestId, children }) => (
  <section className="rounded-xl border border-slate-200 bg-white p-5">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <h3 className="font-heading text-sm font-bold uppercase tracking-wide text-slate-800">{title}</h3>
      <StatusBadge status={status} testId={statusTestId} />
    </div>
    <div className="mt-3">{children}</div>
  </section>
);

const Fact = ({ children }) => <p className="text-sm text-slate-800">{children}</p>;
const Interpretation = ({ children }) => (
  <p className="mt-2 text-sm italic text-slate-500">{children}</p>
);
const ProReview = ({ children }) => (
  <p className="mt-2 flex items-start gap-1.5 text-sm text-blue-800">
    <ShieldAlert size={15} className="mt-0.5 shrink-0" />{children}
  </p>
);

export default function ReportPrototypePage() {
  const [resolution, setResolution] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    api.get("/parcels/resolve", { params: { id: CONTROL_PARCEL }, timeout: 60000 })
      .then((r) => setResolution(r.data))
      .catch(() => setFailed(true));
  }, []);

  const p = resolution?.parcel;
  const planning = resolution?.planning;

  return (
    <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6" data-testid="report-prototype-page">
      <div className="mb-6 flex items-start gap-3 rounded-xl border-2 border-dashed border-amber-400 bg-amber-50 p-4" data-testid="prototype-banner">
        <FlaskConical size={20} className="mt-0.5 shrink-0 text-amber-600" />
        <div>
          <p className="font-heading text-sm font-bold text-amber-900">
            PROTOTYP — rozszerzony raport due diligence działki
          </p>
          <p className="mt-1 text-xs text-amber-800">
            Układ docelowy. Sekcje oznaczone „zweryfikowane live” korzystają z prawdziwych odpowiedzi źródeł
            urzędowych (2026-09-18); pozostałe są poglądowymi placeholderami. Żadna treść nie jest wnioskiem
            prawnym. Trasa niedostępna z nawigacji.
          </p>
        </div>
      </div>

      <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#DC143C]">PlanGmina — prototyp</p>
      <h1 className="mt-1 font-heading text-3xl font-bold tracking-tight text-slate-900">
        Raport Due Diligence — {CONTROL_PARCEL}
      </h1>

      {!resolution && !failed && (
        <p className="mt-8 text-sm text-slate-500"><Loader2 size={15} className="mr-2 inline animate-spin" />Ładowanie danych urzędowych…</p>
      )}
      {failed && (
        <p className="mt-8 rounded-lg border border-rose-300 bg-rose-50 p-4 text-sm text-rose-800">
          Nie udało się pobrać danych działki (prototyp).
        </p>
      )}

      {p && (
        <div className="mt-8 space-y-4">
          <Section title="Środowisko planistyczne" status={planning?.mpzp?.status === "existing" ? "DETECTED" : "NOT_DETECTED"} statusTestId="proto-planning-status">
            <Fact>
              <BadgeCheck size={14} className="mr-1 inline text-emerald-600" />
              Zweryfikowane live: {p.id} — gmina {p.gmina_name}, powiat {p.powiat}, woj. {p.wojewodztwo}.
              Geometria: {p.has_geometry ? "dostępna (EPSG:2180)" : "brak"}.
              Plan ogólny: {planning?.plan_ogolny?.status && planning.plan_ogolny.status !== "unknown" ? planning.plan_ogolny.status : "brak danych"} · MPZP: {planning?.mpzp?.status && planning.mpzp.status !== "unknown" ? planning.mpzp.status : "brak danych"}.
            </Fact>
            <Interpretation>
              Interpretacja: statusy planistyczne pochodzą z krajowych usług WMS; „brak danych" oznacza brak
              geometrii w usłudze krajowej, nie brak planu.
            </Interpretation>
            <ProReview>Ostateczny stan prawny potwierdza się w ewidencji gminy / Rejestrze Urbanistycznym.</ProReview>
            <Provenance>GUGiK ULDK + KI_POG/KI_MPZP WMS · pobrano przy renderze (live) · metoda: GetParcelById + WMS bbox · pokrycie: krajowe, zależne od publikacji gmin.</Provenance>
          </Section>

          <Section title="Powódź / woda" status="DETECTED" statusTestId="proto-flood-status">
            <Fact>
              Zweryfikowane live (2026-09-18): w sąsiedztwie działki (bbox +50 m) usługa Wody Polskie ISOK zwraca
              4 obiekty mapy zagrożenia powodziowego (HazardArea) i 1 strefę ryzyka (RiskZone).
            </Fact>
            <Interpretation>
              Interpretacja: wynik oparty na przybliżeniu bbox; wersja produkcyjna użyje dokładnego przecięcia
              geometrii działki. Oznacza to obszar objęty mapowaniem zagrożenia — nie orzeczenie o ryzyku zalania.
            </Interpretation>
            <ProReview>Przy transakcji zalecana weryfikacja przez rzeczoznawcę / zapytanie do Wody Polskie.</ProReview>
            <Provenance>Wody Polskie ISOK MZP/MRP · WFS 2.0.0 wody.isok.gov.pl · RESULTTYPE=hits · pobrano 2026-09-18 · cykl aktualizacji ustawowy (dyrektywa powodziowa).</Provenance>
          </Section>

          <Section title="Środowisko przyrodnicze (Natura 2000, parki, rezerwaty)" status="SOURCE_UNAVAILABLE" statusTestId="proto-env-status">
            <Fact>
              Źródło GDOŚ (sdi.gdos.gov.pl) odpowiedziało błędem dostępu (403, zapora Incapsula) przy zapytaniu
              serwerowym — integracja wymaga oficjalnego odblokowania dostępu.
            </Fact>
            <Interpretation>
              Sekcja celowo pokazuje degradację kontrolowaną: niedostępność źródła nigdy nie jest prezentowana
              jako brak obiektów chronionych.
            </Interpretation>
            <ProReview>Do czasu integracji: sprawdź Geoserwis GDOŚ ręcznie przed decyzją transakcyjną.</ProReview>
            <Provenance>GDOŚ Geoserwis WFS/WMS · ostatnia próba 2026-09-18 · status: UNAVAILABLE (WAF) · pokrycie docelowe: krajowe.</Provenance>
          </Section>

          <Section title="Geologia / osuwiska" status="INSUFFICIENT_DATA" statusTestId="proto-geo-status">
            <Fact>
              PIG-PIB SOPO publikuje wyłącznie usługę przeglądania (WMS); zapytanie punktowe o działkę nie jest
              obsługiwane (zweryfikowane 2026-09-18). Region kontrolny (Mokotów/Śródmieście) nie leży w strefie
              osuwisk — brak wniosku na poziomie działki.
            </Fact>
            <ProReview>Dla regionów górskich i nadmorskich: mapa SOPO + ewentualna opinia geotechniczna.</ProReview>
            <Provenance>PIG-PIB SOPO WMS (cbdgmapa.pgi.gov.pl) · metoda docelowa: GetFeatureInfo/kontakt CBDG · status: INSUFFICIENT_DATA dla zapytań działkowych.</Provenance>
          </Section>

          <Section title="Rolnictwo / gleby" status="INSUFFICIENT_DATA" statusTestId="proto-soil-status">
            <Fact>
              Mapa glebowo-rolnicza (GUGiK WMS) istnieje i zawiera warstwę zasięgu (pokrycie częściowe,
              arkusze 1:5000) — klasyfikacja gleby działki wymaga GetFeatureInfo i walidacji pokrycia.
            </Fact>
            <ProReview>Grunty rolne klas I–III podlegają ograniczeniom zabudowy — potwierdzenie w gminie.</ProReview>
            <Provenance>GUGiK WMS MapaGlebowoRolnicza · pobrano capabilities 2026-09-18 · pokrycie: częściowe (PARTIAL_COVERAGE).</Provenance>
          </Section>

          <Section title="Dziedzictwo / konserwacja" status="INSUFFICIENT_DATA" statusTestId="proto-heritage-status">
            <Fact>
              NID udostępnia WMS/WFS zabytków (usluga.zabytek.gov.pl) — endpoint zweryfikowany, enumeracja
              warstw w toku. Sekcja poglądowa.
            </Fact>
            <ProReview>Wpis do rejestru zabytków lub strefa ochronna istotnie wpływa na możliwości zabudowy — weryfikacja u konserwatora.</ProReview>
            <Provenance>NID INSPIRE WMS/WFS · status: druga fala (P1) · pokrycie: krajowe.</Provenance>
          </Section>

          <Section title="Kontekst fizyczny (BDOT10k / wysokość)" status="REQUIRES_PROFESSIONAL_VERIFICATION" statusTestId="proto-physical-status">
            <Fact>
              Usługa wysokościowa GUGiK NMT odpowiada punktowo (~0,3 s); walidacja semantyki wartości przed
              publikacją. BDOT10k (budynki, drogi, wody) jako kontekst mapowy w fazie P2.
            </Fact>
            <Interpretation>Wysokość i pokrycie terenu to kontekst interpretacyjny, nie fakt prawny.</Interpretation>
            <Provenance>GUGiK services.gugik.gov.pl/nmt · pobrano 2026-09-18 · walidacja jednostek w toku.</Provenance>
          </Section>

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-xs text-slate-500" data-testid="prototype-legal">
            PlanGmina nie jest organem administracji publicznej i nie wydaje decyzji ani opinii prawnych.
            Raport ma charakter informacyjny. Żadna sekcja nie stanowi wniosku „można budować" / „bezpieczne" —
            każda wskazuje wyłącznie stan źródła urzędowego i zakres wymaganej weryfikacji specjalisty.
          </div>

          <div className="pt-2">
            <Link to={`/dzialka/${CONTROL_PARCEL.replaceAll("/", "~")}`}>
              <Button variant="outline" size="sm" data-testid="prototype-back">Wróć do strony działki</Button>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
